import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import moment from "moment";
import ReactPaginate from "react-paginate";
import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";

import Sidebar from "../sidebar";
import {
  deleteSampleCategory,
  getSampleCategories,
} from "./sample-categories-section.service";
import Toggle from "react-toggle";
import { updateSampleCategory } from "./add-and-edit-sample-category-page/form-section.service";
import Swal from "sweetalert2";
import { AuthContext } from "../../../contexts/AuthContext";

export default function SampleCategoriesSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageParam = queryParams.get("page");
  const [currentPage, setCurrentPage] = useState(
    pageParam ? parseInt(pageParam) : 1
  );
  const queryClient = useQueryClient();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(parseInt(pageParam));
    }
  }, [pageParam]);

  const {
    isPending,
    isError,
    data: response,
    error,
  } = useQuery({
    queryKey: ["admin-sample-categories", currentPage],
    queryFn: () => getSampleCategories({ page: currentPage }),
    placeholderData: keepPreviousData,
  });

  const handlePageChange = (selectedItem: { selected: number }) => {
    const newPage = selectedItem.selected + 1;
    setCurrentPage(newPage);
    navigate(`?page=${newPage}`);
  };

  const handleDeleteSampleCategory = async (categoryId: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      await deleteSampleCategory(categoryId);
      queryClient.invalidateQueries({ queryKey: ["admin-sample-categories"] });
      Swal.fire("Deleted!", "The sample category has been deleted.", "success");
    }
  };

  const handleToggleCategory = async (
    categoryId: string,
    isActive: boolean
  ) => {
    await updateSampleCategory(categoryId, {
      isActive: !isActive,
    });
    queryClient.invalidateQueries({ queryKey: ["admin-sample-categories"] });
  };

  const isBackendUser = user?.userType === "Back end Entry User";
  const isSuperAdmin = user?.userType === "Super Admin";

  return (
    <section className="ec-page-content ec-vendor-dashboard section-space-p">
      <div className="container">
        <div className="row">
          <Sidebar />
          <div className="ec-shop-rightside col-lg-9 col-md-12">
            <div className="ec-vendor-dashboard-card space-bottom-30">
              <div className="ec-vendor-card-header">
                <h5>Sample Categories</h5>
                <div className="ec-header-btn">
                  <Link className="btn btn-lg btn-primary" to="add">
                    Add Sample Category
                  </Link>
                </div>
              </div>
              <div className="ec-vendor-card-body">
                <div className="ec-vendor-card-table">
                  <table className="table ec-table">
                    <thead>
                      <tr>
                        <th scope="col">Category Image</th>
                        <th scope="col">Category Code</th>
                        <th scope="col">Category Name</th>
                        <th scope="col">Description</th>
                        <th scope="col">Status</th>
                        <th scope="col">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {response?.data.results.map((category: any) => (
                        <tr key={category.id}>
                          <td>
                            <img
                              src={`https://d1gdand68muq63.cloudfront.net/sample-categories/96/${category?.image?.key}`}
                              alt={category.name}
                              width="80"
                            />
                          </td>
                          <td>
                            <span>{category.code}</span>
                          </td>
                          <td>
                            <span>{category.name}</span>
                          </td>
                          <td>
                            <span>{category.description}</span>
                          </td>
                          <td>
                            <span>
                              <Toggle
                                defaultChecked={category.isActive}
                                icons={false}
                                onChange={() => {
                                  handleToggleCategory(
                                    category.id,
                                    category.isActive
                                  );
                                }}
                              />
                            </span>
                          </td>
                          <td>
                            <span>
                              <Link
                                to={`edit/${category.id}`}
                                className="ec-action-btn mr-1"
                              >
                                <i className="fa fa-edit fs-5"></i>
                              </Link>
                              {isSuperAdmin && (
                                <Link
                                  onClick={() => {
                                    handleDeleteSampleCategory(category.id);
                                  }}
                                  to={""}
                                  className="ec-action-btn text-danger "
                                >
                                  <i className="fa fa-trash fs-5"></i>
                                </Link>
                              )}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="col-lg-12">
                  <ReactPaginate
                    nextLabel=">>"
                    onPageChange={handlePageChange}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={2}
                    pageCount={response?.data.totalPages}
                    forcePage={currentPage - 1}
                    previousLabel="<<"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="page-item"
                    previousLinkClassName="page-link"
                    nextClassName="page-item"
                    nextLinkClassName="page-link"
                    breakLabel="..."
                    breakClassName="page-item"
                    breakLinkClassName="page-link"
                    containerClassName="pagination"
                    activeClassName="active"
                    renderOnZeroPageCount={null}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
